import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { z } from 'zod';
import { zodResolver } from '@hookform/resolvers/zod';
import { GraduationCap } from 'lucide-react';
import { useAuthStore } from '@/stores/auth.store';
import { AuthApi, isOrgResult } from '@/services/auth.api';

const schema = z.object({
  login: z.string().min(1, 'Username, email, or mobile is required'),
  password: z.string().min(1, 'Password is required'),
});
type Form = z.infer<typeof schema>;

/**
 * Single login form for org admins and direct school users — the backend
 * (POST /auth/school/login) decides which one a login+password belongs to.
 * See docs/DEVIATIONS.md.
 */
export function LoginPage() {
  const navigate = useNavigate();
  const login = useAuthStore((s) => s.login);
  const logout = useAuthStore((s) => s.logout);
  const setOrgSession = useAuthStore((s) => s.setOrgSession);
  const [err, setErr] = useState<string | null>(null);

  const {
    register,
    handleSubmit,
    formState: { errors, isSubmitting },
  } = useForm<Form>({
    resolver: zodResolver(schema),
    defaultValues: { login: '', password: '' },
  });

  const onSubmit = async (values: Form) => {
    setErr(null);
    try {
      const result = await AuthApi.login(values.login.trim(), values.password);

      // Clear any prior session so no stale tenant/org header leaks in.
      logout();

      if (isOrgResult(result)) {
        setOrgSession({
          user: {
            id: result.admin.id,
            name: result.admin.name,
            email: result.admin.email ?? '',
            role: result.admin.role,
            organizationId: result.admin.organizationId,
            scope: 'organization',
          },
          accessToken: result.tokens.accessToken,
          refreshToken: result.tokens.refreshToken,
        });
        navigate('/org');
        return;
      }

      login({
        user: {
          id: result.user.id,
          name: result.user.name,
          email: result.user.email ?? '',
          role: result.user.role,
          schoolId: result.user.schoolId,
          schoolSlug: result.user.schoolSlug,
          scope: 'tenant',
        },
        accessToken: result.tokens.accessToken,
        refreshToken: result.tokens.refreshToken,
        schoolSlug: result.school.slug,
      });
      navigate('/dashboard');
    } catch {
      // Generic on purpose — never reveal whether a login exists.
      setErr('Invalid username or password');
    }
  };

  return (
    <div className="flex min-h-screen items-center justify-center bg-gradient-to-br from-brand-50 via-white to-brand-100 p-4">
      <form
        onSubmit={handleSubmit(onSubmit)}
        className="w-full max-w-sm rounded-xl bg-white p-8 shadow-xl"
      >
        <div className="mb-6 flex items-center gap-3">
          <div className="rounded-lg bg-brand-600 p-2 text-white">
            <GraduationCap className="h-6 w-6" />
          </div>
          <div>
            <h1 className="text-xl font-bold text-slate-900">EduPro</h1>
            <p className="text-xs text-slate-500">Sign in</p>
          </div>
        </div>

        <label className="mb-3 block">
          <span className="mb-1 block text-sm font-medium text-slate-700">
            Username, email, or mobile
          </span>
          <input
            {...register('login')}
            className="w-full rounded-md border border-slate-300 px-3 py-2"
            autoComplete="username"
            autoFocus
          />
          {errors.login && (
            <span className="mt-1 block text-xs text-red-600">
              {errors.login.message}
            </span>
          )}
        </label>

        <label className="mb-4 block">
          <span className="mb-1 block text-sm font-medium text-slate-700">
            Password
          </span>
          <input
            type="password"
            {...register('password')}
            className="w-full rounded-md border border-slate-300 px-3 py-2"
            autoComplete="current-password"
          />
          {errors.password && (
            <span className="text-xs text-red-600">
              {errors.password.message}
            </span>
          )}
        </label>

        {err && (
          <div className="mb-3 rounded-md bg-red-50 px-3 py-2 text-sm text-red-700">
            {err}
          </div>
        )}

        <button
          type="submit"
          disabled={isSubmitting}
          className="btn-primary w-full"
        >
          {isSubmitting ? 'Signing in…' : 'Sign in'}
        </button>

        <p className="mt-4 text-center text-xs text-slate-500">
          Platform admin?{' '}
          <a
            href="/superadmin/login"
            className="text-brand-600 hover:underline"
          >
            Superadmin login
          </a>
        </p>
      </form>
    </div>
  );
}
